import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { Play, Pause, RotateCw, Square, Loader } from "lucide-react";

const API_URL = "https://fastapi.dev.torsunov.pro"; // Your API URL

export const StreamingPlayer = ({ mediaId = "df2b873f2a894e82a116283bf1b8c893" }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [streamStatus, setStreamStatus] = useState<{
    status: string;
    current_index: string;
    total_videos: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Start the stream
  const startStream = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/streaming/start_stream?media=${mediaId}&update_interval=120&reset=true`,
        { method: "POST" },
      );
      const data = await response.json();
      if (response.ok) {
        console.log("Stream started:", data);
        // Wait a bit for the playlist to be created
        setTimeout(() => {
          loadPlaylist();
          checkStreamStatus();
        }, 1000);
      } else {
        setError(data.detail || "Failed to start stream");
      }
    } catch (err) {
      setError("Network error: " + (err as unknown as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Load the HLS playlist
  const loadPlaylist = () => {
    const video = videoRef.current;
    if (!video) return;

    const playlistUrl = `${API_URL}/streaming/playlist?media=${mediaId}`;

    if (hlsRef.current) {
      hlsRef.current?.destroy();
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
      });

      hls.loadSource(playlistUrl);
      hls.attachMedia(video);
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().then(() => setIsPlaying(true));
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) {
          console.error("HLS fatal error:", data);
          setError("Playback error: " + data.type);
        }
      });

      // Periodically reload the manifest for live updates
      const reloadInterval = setInterval(() => {
        if (hls.media && !video.ended) {
          hls.startLoad();
        }
      }, 30000); // Every 30 seconds

      return () => clearInterval(reloadInterval);
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // For Safari
      video.src = playlistUrl;
      video.addEventListener("loadedmetadata", () => {
        video.play().then(() => setIsPlaying(true));
      });
    }
  };

  // Check stream status
  const checkStreamStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/streaming/stream_status?media=${mediaId}`);
      const data = await response.json();
      setStreamStatus(data);
    } catch (err) {
      console.error("Failed to check status:", err);
    }
  };

  // Stop the stream
  const stopStream = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/streaming/stop_stream?media=${mediaId}`, {
        method: "POST",
      });
      if (response.ok) {
        console.log("Stream stopped");
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
        setIsPlaying(false);
        setStreamStatus(null);
      }
    } catch (err) {
      setError("Failed to stop stream: " + (err as unknown as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, []);

  // Periodically check stream status
  useEffect(() => {
    if (streamStatus?.status === "active") {
      const interval = setInterval(checkStreamStatus, 10000); // Every 10 seconds
      return () => clearInterval(interval);
    }
  }, [streamStatus?.status, mediaId]);

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="bg-gray-900 rounded-lg shadow-lg overflow-hidden">
        {/* Video Player */}
        <div className="relative aspect-video bg-black">
          <video
            ref={videoRef}
            className="w-full h-full"
            controls={false}
            onEnded={() => setIsPlaying(false)}
          />

          {/* Loading overlay */}
          {isLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
              <Loader className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="bg-gray-800 p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <button
                onClick={startStream}
                disabled={isLoading || streamStatus?.status === "active"}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <RotateCw className="w-4 h-4" />
                Start Stream
              </button>

              <button
                onClick={togglePlayPause}
                disabled={!streamStatus || streamStatus.status !== "active"}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-4 h-4" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Play
                  </>
                )}
              </button>

              <button
                onClick={stopStream}
                disabled={isLoading || streamStatus?.status !== "active"}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Square className="w-4 h-4" />
                Stop Stream
              </button>
            </div>

            {/* Status */}
            {streamStatus && (
              <div className="text-white">
                {streamStatus.status === "active" ? (
                  <span className="text-green-400">
                    ● Live - Segment {streamStatus.current_index}/{streamStatus.total_videos}
                  </span>
                ) : (
                  <span className="text-gray-400">● Offline</span>
                )}
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-2 p-2 bg-red-600 bg-opacity-20 border border-red-600 rounded text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Info */}
          <div className="mt-4 text-gray-400 text-sm">
            <p>Media ID: {mediaId}</p>
            {streamStatus?.status === "active" && <p>New segments are added every 2 minutes</p>}
          </div>
        </div>
      </div>
    </div>
  );
};
